import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import axiosInstance from "../axiosInstance";
import "./UsersSecurity.css";
import PasswordManagement from "./PasswordManagement";

const emptyUser = {
  email: "",
  first_name: "",
  middle_name: "",
  last_name: "",
  phone: "",
  role: "",
  branch: "",
  agent_code: "",
  is_head_office_user: false,
  status: "ACTIVE",
  is_active: true,
  password: "",
};

const emptyRole = {
  name: "",
  description: "",
  is_active: true,
  permission_ids: [],
};

const normaliseList = (response) =>
  response.data?.results || response.data || [];

const messageFromError = (error) => {
  const data = error.response?.data;

  if (!data) return "The request could not be completed.";
  if (typeof data.detail === "string") return data.detail;

  return Object.entries(data)
    .flatMap(([field, messages]) =>
      (Array.isArray(messages) ? messages : [messages]).map(
        (message) => `${field}: ${message}`
      )
    )
    .join(" ");
};

export default function UsersSecurity() {
  const [tab, setTab] = useState("users");
  const [summary, setSummary] = useState({});
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [branches, setBranches] = useState([]);
  const [userForm, setUserForm] = useState(emptyUser);
  const [roleForm, setRoleForm] = useState(emptyRole);
  const [editingUser, setEditingUser] = useState(null);
  const [editingRole, setEditingRole] = useState(null);
  const [userModal, setUserModal] = useState(false);
  const [roleModal, setRoleModal] = useState(false);
  const [search, setSearch] = useState("");
  const [permissionSearch, setPermissionSearch] = useState("");

  const load = async () => {
    const [
      summaryResponse,
      usersResponse,
      rolesResponse,
      permissionsResponse,
      branchesResponse,
    ] = await Promise.all([
      axiosInstance.get("settings/security/summary/"),
      axiosInstance.get("settings/security/users/"),
      axiosInstance.get("settings/security/roles/"),
      axiosInstance.get("settings/security/permissions/"),
      axiosInstance.get("settings/branches/"),
    ]);

    setSummary(summaryResponse.data);
    setUsers(normaliseList(usersResponse));
    setRoles(normaliseList(rolesResponse));
    setPermissions(normaliseList(permissionsResponse));
    setBranches(normaliseList(branchesResponse));
  };

  useEffect(() => {
    load().catch((error) =>
      toast.error(messageFromError(error))
    );
  }, []);

  const permissionGroups = useMemo(() => {
    const query = permissionSearch.trim().toLowerCase();
    const groups = {};

    permissions
      .filter((permission) => {
        if (!query) return true;
        return [
          permission.name,
          permission.description,
          permission.friendly_category,
        ]
          .join(" ")
          .toLowerCase()
          .includes(query);
      })
      .forEach((permission) => {
        const category =
          permission.friendly_category || "Other";

        if (!groups[category]) groups[category] = [];
        groups[category].push(permission);
      });

    return groups;
  }, [permissions, permissionSearch]);

  const filteredUsers = users.filter((user) =>
    [
      user.full_name,
      user.email,
      user.role_name,
      user.branch_name,
      user.agent_code,
    ]
      .join(" ")
      .toLowerCase()
      .includes(search.toLowerCase())
  );

  const openNewUser = () => {
    setEditingUser(null);
    setUserForm(emptyUser);
    setUserModal(true);
  };

  const openUser = (user) => {
    setEditingUser(user.id);
    setUserForm({
      email: user.email || "",
      first_name: user.first_name || "",
      middle_name: user.middle_name || "",
      last_name: user.last_name || "",
      phone: user.phone || "",
      role: user.role || "",
      branch: user.branch || "",
      agent_code: user.agent_code || "",
      is_head_office_user: Boolean(user.is_head_office_user),
      status: user.status || "ACTIVE",
      is_active: Boolean(user.is_active),
      password: "",
    });
    setUserModal(true);
  };

  const saveUser = async (event) => {
    event.preventDefault();

    const payload = {
      ...userForm,
      role: userForm.role || null,
      branch: userForm.branch || null,
    };

    if (!payload.password) delete payload.password;

    try {
      if (editingUser) {
        await axiosInstance.patch(
          `settings/security/users/${editingUser}/`,
          payload
        );
      } else {
        await axiosInstance.post(
          "settings/security/users/",
          payload
        );
      }

      setUserModal(false);
      await load();
      toast.success(
        editingUser ? "User updated." : "User created."
      );
    } catch (error) {
      toast.error(messageFromError(error));
    }
  };

  const openNewRole = () => {
    setEditingRole(null);
    setRoleForm(emptyRole);
    setRoleModal(true);
  };

  const openRole = (role) => {
    setEditingRole(role.id);
    setRoleForm({
      name: role.name,
      description: role.description || "",
      is_active: Boolean(role.is_active),
      permission_ids: (role.permissions || []).map(
        (permission) => permission.id
      ),
      is_system_role: Boolean(role.is_system_role),
    });
    setRoleModal(true);
  };

  const toggleRolePermission = (permissionId) => {
    setRoleForm((current) => ({
      ...current,
      permission_ids: current.permission_ids.includes(permissionId)
        ? current.permission_ids.filter(
            (id) => id !== permissionId
          )
        : [...current.permission_ids, permissionId],
    }));
  };

  const toggleCategory = (categoryPermissions) => {
    const ids = categoryPermissions.map(
      (permission) => permission.id
    );
    const allSelected = ids.every((id) =>
      roleForm.permission_ids.includes(id)
    );

    setRoleForm((current) => ({
      ...current,
      permission_ids: allSelected
        ? current.permission_ids.filter(
            (id) => !ids.includes(id)
          )
        : Array.from(
            new Set([...current.permission_ids, ...ids])
          ),
    }));
  };

  const saveRole = async (event) => {
    event.preventDefault();

    try {
      if (editingRole) {
        await axiosInstance.patch(
          `settings/security/roles/${editingRole}/`,
          roleForm
        );
      } else {
        await axiosInstance.post(
          "settings/security/roles/",
          roleForm
        );
      }

      setRoleModal(false);
      await load();
      toast.success(
        editingRole ? "Role updated." : "Role created."
      );
    } catch (error) {
      toast.error(messageFromError(error));
    }
  };

  return (
    <section className="security-workspace">
      <div className="security-heading">
        <div>
          <span className="eyebrow">Access control</span>
          <h2>Users & Security</h2>
          <p>
            Give each person a clear job role, branch scope and
            only the permissions needed for that work.
          </p>
        </div>
      </div>

      <div className="security-summary">
        <article>
          <i className="bi bi-people"></i>
          <div>
            <span>System users</span>
            <strong>{summary.users || 0}</strong>
          </div>
        </article>
        <article>
          <i className="bi bi-person-check"></i>
          <div>
            <span>Active users</span>
            <strong>{summary.active_users || 0}</strong>
          </div>
        </article>
        <article>
          <i className="bi bi-person-gear"></i>
          <div>
            <span>Active roles</span>
            <strong>{summary.roles || 0}</strong>
          </div>
        </article>
        <article className={
          summary.users_without_role ? "warning" : ""
        }>
          <i className="bi bi-shield-exclamation"></i>
          <div>
            <span>Users without role</span>
            <strong>{summary.users_without_role || 0}</strong>
          </div>
        </article>
      </div>

      <div className="security-tabs">
        {[
          ["users", "people", "Users"],
          ["roles", "person-gear", "Roles"],
          ["permissions", "shield-lock", "Permission Catalogue"],
          ["passwords", "key", "Password Management"],
        ].map(([id, icon, label]) => (
          <button
            key={id}
            className={tab === id ? "active" : ""}
            onClick={() => setTab(id)}
          >
            <i className={`bi bi-${icon}`}></i>
            {label}
          </button>
        ))}
      </div>

      {tab === "users" && (
        <div className="security-card">
          <div className="security-toolbar">
            <div>
              <h3>System users</h3>
              <p>
                Policyholder portal credentials are managed from
                policies, not from this list.
              </p>
            </div>
            <div className="security-toolbar-actions">
              <label className="security-search">
                <i className="bi bi-search"></i>
                <input
                  value={search}
                  onChange={(event) =>
                    setSearch(event.target.value)
                  }
                  placeholder="Search users"
                />
              </label>
              <button
                className="security-primary"
                onClick={openNewUser}
              >
                <i className="bi bi-person-plus"></i>
                Add user
              </button>
            </div>
          </div>

          <div className="security-table-wrap">
            <table>
              <thead>
                <tr>
                  <th>User</th>
                  <th>Role</th>
                  <th>Branch scope</th>
                  <th>Status</th>
                  <th>Access</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => (
                  <tr key={user.id}>
                    <td>
                      <strong>{user.full_name}</strong>
                      <span>{user.email}</span>
                    </td>
                    <td>{user.role_name || "NO ROLE"}</td>
                    <td>
                      {user.is_head_office_user
                        ? "ALL AUTHORISED BRANCHES"
                        : user.branch_name || "NOT ASSIGNED"}
                    </td>
                    <td>
                      <span className={`security-status ${
                        user.is_active ? "active" : "inactive"
                      }`}>
                        {user.is_active
                          ? user.status
                          : "INACTIVE"}
                      </span>
                    </td>
                    <td>
                      {(user.permission_slugs || []).length === 1 &&
                      user.permission_slugs[0] === "*"
                        ? "FULL ACCESS"
                        : `${(user.permission_slugs || []).length} permissions`}
                    </td>
                    <td>
                      <button
                        className="security-icon"
                        title="Edit user"
                        onClick={() => openUser(user)}
                      >
                        <i className="bi bi-pencil-square"></i>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "roles" && (
        <div className="security-card">
          <div className="security-toolbar">
            <div>
              <h3>Roles</h3>
              <p>
                Start with a system role or create a role for a
                special job in the organisation.
              </p>
            </div>
            <button
              className="security-primary"
              onClick={openNewRole}
            >
              <i className="bi bi-plus-circle"></i>
              Create role
            </button>
          </div>

          <div className="role-grid">
            {roles.map((role) => (
              <article key={role.id} className="role-card">
                <div className="role-card-head">
                  <div className="role-icon">
                    <i className="bi bi-person-lock"></i>
                  </div>
                  <div>
                    <h4>{role.name}</h4>
                    <span>
                      {role.is_system_role
                        ? "SYSTEM ROLE"
                        : "CUSTOM ROLE"}
                    </span>
                  </div>
                </div>
                <p>
                  {role.description ||
                    "No role description has been added."}
                </p>
                <div className="role-stats">
                  <span>
                    <strong>
                      {(role.permissions || []).length}
                    </strong>
                    Permissions
                  </span>
                  <span>
                    <strong>{role.user_count || 0}</strong>
                    Users
                  </span>
                </div>
                <button onClick={() => openRole(role)}>
                  Review permissions
                  <i className="bi bi-arrow-right"></i>
                </button>
              </article>
            ))}
          </div>
        </div>
      )}

      {tab === "permissions" && (
        <div className="security-card">
          <div className="security-toolbar">
            <div>
              <h3>Permission catalogue</h3>
              <p>
                Permissions are written as actions users can
                understand. Technical permission codes remain hidden.
              </p>
            </div>
            <label className="security-search">
              <i className="bi bi-search"></i>
              <input
                value={permissionSearch}
                onChange={(event) =>
                  setPermissionSearch(event.target.value)
                }
                placeholder="Search permissions"
              />
            </label>
          </div>

          <div className="permission-catalogue">
            {Object.entries(permissionGroups).map(
              ([category, entries]) => (
                <section key={category}>
                  <h4>
                    {category}
                    <span>{entries.length}</span>
                  </h4>
                  <div>
                    {entries.map((permission) => (
                      <article key={permission.id}>
                        <div>
                          <strong>{permission.name}</strong>
                          <p>{permission.description}</p>
                        </div>
                        <span className={`risk ${
                          permission.risk_level || "STANDARD"
                        }`}>
                          {permission.risk_level || "STANDARD"}
                        </span>
                      </article>
                    ))}
                  </div>
                </section>
              )
            )}
          </div>
        </div>
      )}

      {userModal && (
        <div className="security-modal-backdrop">
          <form
            className="security-modal"
            onSubmit={saveUser}
          >
            <header>
              <div>
                <span className="eyebrow">User access</span>
                <h3>
                  {editingUser ? "Edit user" : "Add system user"}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setUserModal(false)}
              >
                <i className="bi bi-x-lg"></i>
              </button>
            </header>

            <div className="security-form-grid">
              <label>
                <span>First name</span>
                <input
                  value={userForm.first_name}
                  onChange={(event) =>
                    setUserForm({
                      ...userForm,
                      first_name: event.target.value.toUpperCase(),
                    })
                  }
                  required
                />
              </label>
              <label>
                <span>Middle name</span>
                <input
                  value={userForm.middle_name}
                  onChange={(event) =>
                    setUserForm({
                      ...userForm,
                      middle_name: event.target.value.toUpperCase(),
                    })
                  }
                />
              </label>
              <label>
                <span>Last name</span>
                <input
                  value={userForm.last_name}
                  onChange={(event) =>
                    setUserForm({
                      ...userForm,
                      last_name: event.target.value.toUpperCase(),
                    })
                  }
                  required
                />
              </label>
              <label>
                <span>Email</span>
                <input
                  type="email"
                  value={userForm.email}
                  onChange={(event) =>
                    setUserForm({
                      ...userForm,
                      email: event.target.value.toLowerCase(),
                    })
                  }
                  required
                />
              </label>
              <label>
                <span>Phone</span>
                <input
                  value={userForm.phone}
                  onChange={(event) =>
                    setUserForm({
                      ...userForm,
                      phone: event.target.value,
                    })
                  }
                />
              </label>
              <label>
                <span>Role</span>
                <select
                  value={userForm.role}
                  onChange={(event) =>
                    setUserForm({
                      ...userForm,
                      role: event.target.value,
                    })
                  }
                  required
                >
                  <option value="">SELECT ROLE</option>
                  {roles
                    .filter((role) => role.is_active)
                    .map((role) => (
                      <option key={role.id} value={role.id}>
                        {role.name}
                      </option>
                    ))}
                </select>
              </label>
              <label>
                <span>Branch</span>
                <select
                  value={userForm.branch}
                  onChange={(event) =>
                    setUserForm({
                      ...userForm,
                      branch: event.target.value,
                    })
                  }
                  disabled={userForm.is_head_office_user}
                >
                  <option value="">SELECT BRANCH</option>
                  {branches
                    .filter((branch) => branch.is_active)
                    .map((branch) => (
                      <option
                        key={branch.id}
                        value={branch.id}
                      >
                        {branch.code} — {branch.name}
                      </option>
                    ))}
                </select>
              </label>
              <label>
                <span>Agent code</span>
                <input
                  value={userForm.agent_code}
                  onChange={(event) =>
                    setUserForm({
                      ...userForm,
                      agent_code: event.target.value.toUpperCase(),
                    })
                  }
                  placeholder="Only for sales agents"
                />
              </label>
              <label className="wide">
                <span>
                  {editingUser
                    ? "New password (optional)"
                    : "Temporary password"}
                </span>
                <input
                  type="password"
                  minLength="8"
                  value={userForm.password}
                  onChange={(event) =>
                    setUserForm({
                      ...userForm,
                      password: event.target.value,
                    })
                  }
                  required={!editingUser}
                />
              </label>
              <label className="security-check">
                <input
                  type="checkbox"
                  checked={userForm.is_head_office_user}
                  onChange={(event) =>
                    setUserForm({
                      ...userForm,
                      is_head_office_user: event.target.checked,
                      branch: event.target.checked
                        ? ""
                        : userForm.branch,
                    })
                  }
                />
                <span>
                  Head-office access across authorised branches
                </span>
              </label>
              <label className="security-check">
                <input
                  type="checkbox"
                  checked={userForm.is_active}
                  onChange={(event) =>
                    setUserForm({
                      ...userForm,
                      is_active: event.target.checked,
                      status: event.target.checked
                        ? "ACTIVE"
                        : "INACTIVE",
                    })
                  }
                />
                <span>User can sign in</span>
              </label>
            </div>

            <footer>
              <button
                type="button"
                className="secondary"
                onClick={() => setUserModal(false)}
              >
                Cancel
              </button>
              <button className="security-primary">
                <i className="bi bi-check2-circle"></i>
                Save user
              </button>
            </footer>
          </form>
        </div>
      )}

      {tab === "passwords" && <PasswordManagement />}

      {roleModal && (
        <div className="security-modal-backdrop">
          <form
            className="security-modal role-editor"
            onSubmit={saveRole}
          >
            <header>
              <div>
                <span className="eyebrow">Role builder</span>
                <h3>
                  {editingRole ? "Review role" : "Create role"}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setRoleModal(false)}
              >
                <i className="bi bi-x-lg"></i>
              </button>
            </header>

            <div className="role-editor-details">
              <label>
                <span>Role name</span>
                <input
                  value={roleForm.name}
                  disabled={roleForm.is_system_role}
                  onChange={(event) =>
                    setRoleForm({
                      ...roleForm,
                      name: event.target.value.toUpperCase(),
                    })
                  }
                  required
                />
              </label>
              <label className="wide">
                <span>What this role is for</span>
                <textarea
                  value={roleForm.description}
                  onChange={(event) =>
                    setRoleForm({
                      ...roleForm,
                      description: event.target.value,
                    })
                  }
                />
              </label>
            </div>

            <div className="role-permission-heading">
              <div>
                <h4>Allowed actions</h4>
                <p>
                  Tick only the work this role should perform.
                </p>
              </div>
              <strong>
                {roleForm.permission_ids.length} selected
              </strong>
            </div>

            <div className="role-permission-groups">
              {Object.entries(permissionGroups).map(
                ([category, entries]) => {
                  const selected = entries.filter((permission) =>
                    roleForm.permission_ids.includes(permission.id)
                  ).length;

                  return (
                    <section key={category}>
                      <button
                        type="button"
                        onClick={() => toggleCategory(entries)}
                      >
                        <span>{category}</span>
                        <small>
                          {selected}/{entries.length}
                        </small>
                      </button>
                      <div>
                        {entries.map((permission) => (
                          <label key={permission.id}>
                            <input
                              type="checkbox"
                              checked={roleForm.permission_ids.includes(
                                permission.id
                              )}
                              onChange={() =>
                                toggleRolePermission(permission.id)
                              }
                            />
                            <span>
                              <strong>{permission.name}</strong>
                              <small>{permission.description}</small>
                            </span>
                            <em className={`risk ${
                              permission.risk_level || "STANDARD"
                            }`}>
                              {permission.risk_level || "STANDARD"}
                            </em>
                          </label>
                        ))}
                      </div>
                    </section>
                  );
                }
              )}
            </div>

            <footer>
              <label className="security-check">
                <input
                  type="checkbox"
                  checked={roleForm.is_active}
                  onChange={(event) =>
                    setRoleForm({
                      ...roleForm,
                      is_active: event.target.checked,
                    })
                  }
                />
                <span>Role is available for assignment</span>
              </label>
              <div>
                <button
                  type="button"
                  className="secondary"
                  onClick={() => setRoleModal(false)}
                >
                  Cancel
                </button>
                <button className="security-primary">
                  <i className="bi bi-shield-check"></i>
                  Save role
                </button>
              </div>
            </footer>
          </form>
        </div>
      )}
    </section>
  );
}
